import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe, JsonPipe, KeyValuePipe, NgTemplateOutlet } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { startCase } from 'lodash-es';
import { Capture } from '../../../core/models/capture.model';
import { CaptureAuditReport, CaptureAuditTransitionerEligibility } from '../../../core/models/capture-audit-report.model';
import { CaptureService } from '../../../core/services/capture.service';
import { UserPresenceService } from 'app/core/services/user-presence.service';
import { CaptureStatusPipe } from '../../../shared/pipes/capture-status.pipe';
import { CaptureStatusReasonPipe } from '../../../shared/pipes/capture-status-reason.pipe';

export class LabeledValue {
  label: string;
  value: any;
  // true when the value is an ISO date or timestamp string
  isDate?: boolean;
  // detail page for the entity identified by value
  link?: string;
}

// builds the detail page url for an id, keyed by the attribute that holds the id
type DetailLinks = Record<string, (value: any) => string>;

const captureLink = (id: number) => `/capture-admin/captures/${id}`;
const providerLink = (npi: string) => `/capture-admin/providers/${npi}`;
const consultNoteLink = (id: number) => `/capture-admin/consult-notes/${id}`;

const OVERVIEW_LINKS: DetailLinks = {
  id: captureLink
};

const IDENTIFIER_LINKS: DetailLinks = {
  patientId: (id: number) => `/capture-admin/patients/${id}`,
  prescriberNpi: providerLink,
  clientId: (id: number) => `/capture-admin/client-management/clients/${id}`,
  parentId: captureLink,
  verifyingPatientAttachmentId: consultNoteLink
};

const PROVIDER_LINKS: DetailLinks = {
  npi: providerLink
};

const PATIENT_ATTACHMENT_LINKS: DetailLinks = {
  id: consultNoteLink,
  inboundFaxId: (id: number) => `/capture-admin/communications/inbound-faxes/${id}`
};

@Component({
  selector: 'app-capture-audit',
  templateUrl: './capture-audit.component.html',
  styleUrls: ['./capture-audit.component.scss'],
  imports: [
    CaptureStatusPipe,
    CaptureStatusReasonPipe,
    MatProgressSpinner,
    NgTemplateOutlet,
    RouterLink,
    DatePipe,
    JsonPipe,
    KeyValuePipe
  ]
})
export class CaptureAuditComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private captureService = inject(CaptureService);
  private router = inject(Router);
  private titleService = inject(Title);
  private userPresenceService = inject(UserPresenceService);

  captureId: string;
  capture: Capture;
  report: CaptureAuditReport;

  overview: LabeledValue[] = [];
  statusTimestamps: LabeledValue[] = [];
  identifiers: LabeledValue[] = [];
  provider: LabeledValue[] = [];
  verifyingPatientAttachment: LabeledValue[] = [];

  loadingCapture = false;
  loadingReport = false;
  captureError: string = null;
  reportError: string = null;

  captureChangedSubscription = null;

  ngOnInit() {
    this.captureId = this.route.snapshot.paramMap.get('id');

    this.loadCapture(this.captureId);
    this.loadReport(this.captureId);

    this.captureChangedSubscription = this.captureService.captureChanged.subscribe(({ capture }) => {
      // is it our capture?
      if (capture.id === this.capture?.id) {
        this.capture = capture;
        this.loadReport(capture.id);
      }
    });

    this.userPresenceService.subscribeAndJoin("capture", this.captureId);
  }

  ngOnDestroy() {
    if (this.captureChangedSubscription) {
      this.captureChangedSubscription.unsubscribe();
    }

    this.userPresenceService.leaveAndUnsubscribe("capture", this.captureId);
  }

  onRefreshClick() {
    this.loadReport(this.captureId);
  }

  onRetryClick() {
    if (this.captureError) {
      this.loadCapture(this.captureId);
    }
    this.loadReport(this.captureId);
  }

  get loadErrors(): string[] {
    return [this.captureError, this.reportError].filter(Boolean);
  }

  isBoolean(value: any): boolean {
    return typeof value === 'boolean';
  }

  isObject(value: any): boolean {
    return value !== null && typeof value === 'object';
  }

  isBlank(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  eligibilityRowClass(row: CaptureAuditTransitionerEligibility): string {
    if (row.result === 'unknown') {
      return 'unknown-row';
    } else if (row.result === 'error') {
      return 'table-danger';
    } else if (row.result === 'would_transition') {
      return 'table-success fw-semibold';
    } else if (this.report.transitioners.targetingCurrentStatus.includes(row.name)) {
      return 'table-warning';
    } else {
      return '';
    }
  }

  private loadCapture(captureId): void {
    this.loadingCapture = true;
    this.captureError = null;
    this.captureService.getCapture(captureId).subscribe(
      capture => {
        this.capture = capture;
        this.loadingCapture = false;
        this.setTitle();
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.router.navigate(['/404']);
        } else {
          console.error(err);
          this.loadingCapture = false;
          this.captureError = this.loadErrorMessage('capture', err);
        }
      }
    );
  }

  private loadReport(captureId): void {
    this.loadingReport = true;
    this.reportError = null;
    this.captureService.getCaptureAuditReport(captureId).subscribe(
      report => {
        this.report = report;
        this.buildLabeledValues(report);
        this.loadingReport = false;
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.router.navigate(['/404']);
        } else {
          console.error(err);
          this.loadingReport = false;
          // don't leave a previous report on screen as if it were current
          this.report = null;
          this.reportError = this.loadErrorMessage('audit report', err);
        }
      }
    );
  }

  private loadErrorMessage(name: string, err: HttpErrorResponse): string {
    const reason = err.status ? `the server responded with ${err.status}` : 'the server could not be reached';
    return `Could not load the ${name}: ${reason}.`;
  }

  private buildLabeledValues(report: CaptureAuditReport): void {
    const allOverview = this.toLabeledValues(report.capture, OVERVIEW_LINKS);

    // set timestamps first, most recent at the top, followed by the unset timestamps alphabetically
    const timestamps = allOverview.filter(labeledValue => labeledValue.isDate && labeledValue.label.endsWith(' At'));
    const setTimestamps = timestamps.
      filter(labeledValue => !this.isBlank(labeledValue.value)).
      sort((a, b) => new Date(b.value).getTime() - new Date(a.value).getTime());
    const unsetTimestamps = timestamps.
      filter(labeledValue => this.isBlank(labeledValue.value)).
      sort((a, b) => a.label.localeCompare(b.label));
    this.statusTimestamps = [...setTimestamps, ...unsetTimestamps];
    this.overview = allOverview.filter(labeledValue => !labeledValue.label.endsWith(' At'));

    this.identifiers = this.toLabeledValues(report.identifiers, IDENTIFIER_LINKS);
    this.provider = this.toLabeledValues(report.provider, PROVIDER_LINKS);
    this.verifyingPatientAttachment = report.verifyingPatientAttachment ?
      this.toLabeledValues(report.verifyingPatientAttachment, PATIENT_ATTACHMENT_LINKS) :
      [];
  }

  private toLabeledValues(attrs: object, links: DetailLinks = {}): LabeledValue[] {
    return Object.entries(attrs).map(([key, value]) => ({
      label: this.toLabel(key),
      value,
      isDate: /(At|Date)$/.test(key),
      link: links[key] && !this.isBlank(value) ? links[key](value) : null
    }));
  }

  // startCase produces "Patient Id" and "Prescriber Npi", so upcase those acronyms
  private toLabel(key: string): string {
    return startCase(key).replace(/\b(Id|Npi)\b/g, acronym => acronym.toUpperCase());
  }

  private setTitle() {
    const title = `NuvemRx 340B Recovery - ${this.capture.candidate.claimIdentifier} | Audit`;
    this.titleService.setTitle(title);
  }
}
